import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "chopbox_cart_v1";

const keyOf = (id, variantName) => `${id}__${variantName || ""}`;

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const add = (menuItem, qty = 1, variant = null) => {
    // variant = {name, price, unit?, note?}
    const variantName = variant?.name || null;
    const price = variant?.price ?? menuItem.price;
    const unit = variant?.unit || menuItem.unit || "";
    const newKey = keyOf(menuItem.id, variantName);
    setItems((prev) => {
      const existing = prev.find((p) => p.cartKey === newKey);
      if (existing) {
        return prev.map((p) =>
          p.cartKey === newKey ? { ...p, quantity: Math.min(p.quantity + qty, 50) } : p
        );
      }
      return [...prev, {
        cartKey: newKey,
        id: menuItem.id,
        variant_name: variantName,
        name: menuItem.name,
        image: menuItem.image,
        price,
        unit,
        quantity: qty,
      }];
    });
  };

  const setQuantity = (cartKey, quantity) => {
    setItems((prev) =>
      prev
        .map((p) => (p.cartKey === cartKey ? { ...p, quantity } : p))
        .filter((p) => p.quantity > 0)
    );
  };

  const remove = (cartKey) => setItems((prev) => prev.filter((p) => p.cartKey !== cartKey));
  const clear = () => setItems([]);

  const { count, subtotal } = useMemo(() => {
    let c = 0, s = 0;
    for (const it of items) {
      c += it.quantity;
      s += it.price * it.quantity;
    }
    return { count: c, subtotal: s };
  }, [items]);

  return (
    <CartContext.Provider
      value={{ items, add, setQuantity, remove, clear, count, subtotal, isOpen, setIsOpen }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
